import { useMapsLibrary } from "@vis.gl/react-google-maps";
import { useEffect, useRef, useState } from "react";
import { Address } from "../../../services/axios/jobs";
import {
  CoreInputStyles,
  LIGHT_THEME,
} from "../../../components/Inputs/BasicInput";
import { SimpleLatLng } from "../../../components/SiteMap/Core";

interface PlaceAutocompleteProps {
  onPlaceSelect: (
    place: google.maps.places.PlaceResult,
    address: Address,
    location: SimpleLatLng | null,
  ) => void;
  autoFocus?: boolean;
  value?: string;
  placeholder?: string;
  className?: string;
}

export default function PlaceAutocomplete({
  value,
  onPlaceSelect,
  autoFocus = true,
  placeholder = "Type (or paste) an address...",
  className = `${CoreInputStyles} ${LIGHT_THEME} basic-input`,
}: PlaceAutocompleteProps) {
  const [placeAutocomplete, setPlaceAutocomplete] =
    useState<google.maps.places.Autocomplete | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const places = useMapsLibrary("places");

  useEffect(() => {
    if (!places || !inputRef.current) return;

    const options = {
      fields: ["geometry", "name", "formatted_address", "address_components"],
    };

    setPlaceAutocomplete(new places.Autocomplete(inputRef.current, options));
  }, [places]);

  useEffect(() => {
    if (!placeAutocomplete) return;

    placeAutocomplete.addListener("place_changed", () => {
      const place = placeAutocomplete.getPlace();
      if (place.address_components) {
        const { address, location } = extractAddress(place);
        onPlaceSelect(place, address, location);
      }
    });
  }, [onPlaceSelect, placeAutocomplete]);

  // Keep our input in-sync with upstream value changes:
  useEffect(() => {
    if (inputRef.current && value) {
      inputRef.current.value = value;
    }
  }, [value]);

  return (
    <div className="autocomplete-container">
      <input
        ref={inputRef}
        className={className}
        aria-label="Address"
        placeholder={placeholder}
        name="address_search"
        autoFocus={autoFocus ?? true}
        defaultValue={value}
      />
    </div>
  );
}

export function extractAddress(place: google.maps.places.PlaceResult): {
  address: Address;
  location: SimpleLatLng | null;
} {
  let location: SimpleLatLng | null = null;
  const lat = place.geometry?.location?.lat();
  const lng = place.geometry?.location?.lng();
  if (lat && lng) {
    location = { lat, lng };
  }
  const address: Address = {
    ...extractAddressFromComponents(place),
    name: place.name,
  };
  return { address, location };
}
export function extractAddressFromComponents(hasAddress: {
  address_components?: google.maps.GeocoderAddressComponent[];
}): Address | null {
  const address_components = hasAddress.address_components;
  console.log(address_components);
  if (address_components) {
    const streetNumber = address_components.find((a) =>
      a.types.includes("street_number"),
    )?.long_name;
    const route = address_components.find((a) =>
      a.types.includes("route"),
    )?.long_name;
    const streetLineOne = `${streetNumber ?? ""} ${route ?? ""}`.trim();

    const address: Address = {
      name: streetLineOne.length > 0 ? streetLineOne : undefined,
      city: address_components.find((a) => a.types.includes("locality"))
        ?.long_name,
      state: address_components.find((a) =>
        a.types.includes("administrative_area_level_1"),
      )?.long_name,
      zip: address_components.find((a) => a.types.includes("postal_code"))
        ?.long_name,
      streetLineOne,
      country: address_components.find((a) => a.types.includes("country"))
        ?.long_name,
    };
    return address;
  }
  return null;
}
